export function formatPhone(raw: string): string {
  let digits = raw.replace(/\D/g, "");
  // A pasted or typed country code: an 11-digit number starting with 1 is
  // "+1" plus the 10-digit number (a North American area code can never
  // start with 1), so drop it rather than keeping the first 10 digits and
  // shifting the whole number. Mirrors normalize_phone_input server-side.
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
  digits = digits.slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export function formatSSN(raw: string): string {
  // SSN is exactly 9 digits; cap input so a 10th typed digit doesn't get
  // silently dropped from the display while remaining in the raw string.
  const digits = raw.replace(/\D/g, "").slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

export function formatEIN(raw: string): string {
  // EIN is exactly 9 digits, displayed XX-XXXXXXX.
  const digits = raw.replace(/\D/g, "").slice(0, 9);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}-${digits.slice(2)}`;
}

/** TF `format` values — filters/masks while typing (Wave 1.4). */
export type FieldFormat =
  | "phone"
  | "ssn"
  | "ein"
  | "zip"
  | "integer"
  | "name"
  | "alphanum"
  | "address"
  | "country";

export const FIELD_FORMAT_DEFAULTS: Record<
  FieldFormat,
  {
    inputMode?: "text" | "email" | "tel" | "numeric" | "decimal";
    maxLength?: number;
    placeholder?: string;
  }
> = {
  // 20, not the formatted 12: the cap applies to what's typed or pasted
  // BEFORE formatPhone runs, so "+1 (404) 555-1234" must fit; formatPhone
  // itself never outputs more than 12 characters.
  phone: { inputMode: "tel", maxLength: 20, placeholder: "555-555-1234" },
  ssn: { inputMode: "numeric", maxLength: 11, placeholder: "123-45-6789" },
  ein: { inputMode: "numeric", maxLength: 10, placeholder: "12-3456789" },
  // Plain 5-digit ZIP only -- CardConnect's boarding API rejects a ZIP+4
  // (more than 5 digits), so the field is capped here rather than letting an
  // operator/merchant type it and fail only at submit.
  zip: { inputMode: "numeric", maxLength: 5 },
  integer: { inputMode: "numeric" },
  name: {},
  alphanum: {},
  address: {},
  country: { maxLength: 2, placeholder: "US" },
};

export function applyFieldFormat(format: FieldFormat, raw: string): string {
  switch (format) {
    case "phone":
      return formatPhone(raw);
    case "ssn":
      return formatSSN(raw);
    case "ein":
      return formatEIN(raw);
    case "zip":
    case "integer":
      return numOnly(raw);
    case "name":
      return lettersSpaceOnly(raw);
    case "alphanum":
      return alphaNumSpace(raw);
    case "address":
      return addressSafe(raw);
    case "country":
      return lettersOnly(raw).toUpperCase();
  }
}

/**
 * Masks date entry toward MM/DD/YYYY while the operator types.
 *
 * Normalizes what was typed; never changes which date it means. "3/5/1995"
 * becomes "03/05/1995", but an impossible date like "02/31/2025" is left
 * alone for `dateFieldError` to report — silently sliding it to 03/03 would
 * put a date on a boarding application that nobody entered.
 *
 * Pass `previous` (the current field value) so backspacing over an
 * auto-inserted "/" isn't undone by re-inserting it.
 */
export function formatDateInput(raw: string, previous?: string): string {
  // Operators paste dates out of spreadsheets and CardConnect exports, which
  // hand back ISO. Recognize it before the separators get stripped.
  const iso = /^\s*(\d{4})-(\d{2})-(\d{2})\s*$/.exec(raw);
  if (iso) return `${iso[2]}/${iso[3]}/${iso[1]}`;

  const deleting = previous !== undefined && raw.length < previous.length;
  const cleaned = raw.replace(/[^\d/]/g, "").replace(/\/{2,}/g, "/");

  let month: string;
  let day: string;
  let year: string;
  // Whether the segment is finished — either the operator typed the "/"
  // themselves or they filled all of its digits.
  let monthClosed: boolean;
  let dayClosed: boolean;

  if (cleaned.includes("/")) {
    const segments = cleaned.split("/");
    month = (segments[0] ?? "").slice(0, 2);
    day = (segments[1] ?? "").slice(0, 2);
    year = (segments[2] ?? "").slice(0, 4);
    monthClosed = segments.length > 1;
    dayClosed = segments.length > 2;
  } else {
    const digits = cleaned.slice(0, 8);
    month = digits.slice(0, 2);
    day = digits.slice(2, 4);
    year = digits.slice(4, 8);
    monthClosed = digits.length > 2;
    dayClosed = digits.length > 4;
  }

  // Pad a lone digit only when it can't be the start of a two-digit
  // segment: no month starts with 2-9, no day with 4-9. "1" stays "1"
  // because January and October are both still in play.
  if (month.length === 1 && (monthClosed || (!deleting && month >= "2"))) {
    month = `0${month}`;
    monthClosed = true;
  }
  if (day.length === 1 && (dayClosed || (!deleting && day >= "4"))) {
    day = `0${day}`;
    dayClosed = true;
  }

  if (month.length < 2) return month;
  // Advance to the next segment once one is full, unless the operator is
  // deleting their way back through it.
  let out = month;
  if (!monthClosed && deleting) return out;
  out += "/";

  if (day.length < 2) return out + day;
  out += day;
  if (!dayClosed && deleting) return out;
  return `${out}/${year}`;
}

/** @deprecated Use {@link formatDateInput} — kept for existing call sites. */
export const formatDOB = formatDateInput;

export const numOnly = (v: string) => v.replace(/\D/g, "");
// Currency-override text inputs: allow only characters that can form a dollar
// amount -- digits, a decimal point, a "$" operators habitually type, and the
// "," formatAmountOnBlur inserts as a thousands separator (both stripped
// before the value is parsed or sent, see stripAmountFormatting). Letting the
// comma through here means re-focusing an already-formatted "$1,200.00"
// doesn't mangle it keystroke by keystroke. Blocking letters is the guard
// against entries Number() can't parse ("N/A", "12 dollars", a stray unit) --
// historically the top cause of a rejected manual fee override.
export const feeInputSafe = (v: string) => v.replace(/[^0-9.$,]/g, "");
// Percentage-override text inputs: same idea as feeInputSafe but the decoration
// operators type on a rate is "%", not "$".
export const pctInputSafe = (v: string) => v.replace(/[^0-9.%]/g, "");
// Strip the operator-typed decoration ("$" or "%", plus stray commas /
// whitespace) so what's left is a bare number Number()/parseFloat can parse.
// Applied wherever an override form string is validated or built into the wire
// payload. A currency field never carries "%" and a rate field never carries
// "$", so stripping both from either is safe.
export const stripAmountFormatting = (v: string) => v.replace(/[$%,\s]/g, "");
// On blur, render a fee/rate override the way it reads back: a "$" prefix
// (currency) or "%" suffix (percent). A currency amount also gets thousands
// separators on its integer part ("1200" -> "$1,200.00") -- purely cosmetic,
// same as the "$"; stripAmountFormatting strips the commas back out before
// the value is ever validated, parsed, or sent (see that function and
// feeInputSafe above). Currency truncates -- does not round -- to the
// hundredths place; a rate keeps whatever precision was entered. Either way
// a whole number gains ".00". Blank stays blank; an unparseable value is left
// as-is so its validation error still surfaces. Idempotent: re-formatting
// "$1,200.00" yields "$1,200.00".
export function formatAmountOnBlur(
  raw: string,
  mode: "currency" | "percent",
): string {
  const clean = stripAmountFormatting(raw);
  if (clean === "" || !Number.isFinite(Number(clean))) {
    return clean === "" ? "" : raw;
  }
  const [intRaw, frac = ""] = clean.split(".");
  const intPart = parseInt(intRaw || "0", 10) || 0;
  // String slice, not Math -- truncating via `n * 100` hits float rounding
  // (5.57 * 100 === 556.9999...).
  const decimals =
    mode === "currency" ? frac.slice(0, 2).padEnd(2, "0") : frac.padEnd(2, "0");
  return mode === "currency"
    ? `$${intPart.toLocaleString("en-US")}.${decimals}`
    : `${intPart}.${decimals}%`;
}
export const alphaNumSpace = (v: string) => v.replace(/[^a-zA-Z0-9 ]/g, "");
export const lettersOnly = (v: string) => v.replace(/[^a-zA-Z]/g, "");
export const lettersSpaceOnly = (v: string) => v.replace(/[^a-zA-Z ]/g, "");
export const addressSafe = (v: string) => v.replace(/[^a-zA-Z0-9 ]/g, "");
export const isURL =
  /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;

// Allowed owner titles per ownership type. Keys are CardConnect's
// ownership type codes; values are the subset of title codes that
// CardConnect will accept for that ownership type. Caller MUST guard
// against unknown keys (allowed titles for an unrecognized ownership
// type is the empty set — fall back via `?? []` at the call site).
export const ownerTypeConfirm: Readonly<Record<string, readonly string[]>> = {
  PRTNRSHP: ["PARTNER"],
  PUBCORP: [
    "PRESIDENT",
    "VICE_PRESIDENT",
    "SECRETARY",
    "TREASURER",
    "CEO",
    "CFO",
    "COO",
  ],
  INDIVSOLE: ["OWNER"],
  GOVT: [
    "OWNER",
    "PARTNER",
    "PRESIDENT",
    "VICE_PRESIDENT",
    "MEMBER_LLC",
    "SECRETARY",
    "TREASURER",
    "CEO",
    "CFO",
    "COO",
  ],
  LLC: [
    "PRESIDENT",
    "VICE_PRESIDENT",
    "SECRETARY",
    "TREASURER",
    "CEO",
    "CFO",
    "COO",
    "MEMBER_LLC",
  ],
  NONPRFT: [
    "OWNER",
    "PARTNER",
    "PRESIDENT",
    "VICE_PRESIDENT",
    "MEMBER_LLC",
    "SECRETARY",
    "TREASURER",
    "CEO",
    "CFO",
    "COO",
  ],
  PRIVCORP: [
    "PRESIDENT",
    "VICE_PRESIDENT",
    "SECRETARY",
    "TREASURER",
    "CEO",
    "CFO",
    "COO",
  ],
  TAXEXMPT: [
    "OWNER",
    "PARTNER",
    "PRESIDENT",
    "VICE_PRESIDENT",
    "MEMBER_LLC",
    "SECRETARY",
    "TREASURER",
    "CEO",
    "CFO",
    "COO",
  ],
} as const;

/** Returns the allowed owner titles for an ownership type, or an empty
 *  array if the ownership type isn't recognized. Always safe to .includes()
 *  on the result — never returns undefined. */
export function allowedTitlesForOwnershipType(
  ownershipTypeCd: string,
): readonly string[] {
  return ownerTypeConfirm[ownershipTypeCd] ?? [];
}

// CardConnect builds the owner's legal name as `firstName + " " + lastName`
// and rejects the boarding request (validationErrors code 9834) if the
// result has more than one space -- i.e. either field carries a middle
// name, suffix, or title ("Jr", "III", "PhD"). Guard each field so a
// space anywhere in the trimmed value fails the check.
export const OWNER_NAME_SINGLE_WORD_MESSAGE =
  "Enter a single word — CardConnect rejects a middle name, suffix, or title (Jr, III, PhD) in the owner's legal name.";
export const ownerNameHasSpace = (name: string): boolean =>
  /\s/.test((name ?? "").trim());

// Email regex shared by both the operator form (NewApplication) and the
// public intake form (IntakeForm). The server's check_email() in the
// REST handler + the AppSync resolver are authoritative; this is for
// inline UX feedback only.
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const PHONE_RE = /^[2-9]\d{2}-[2-9]\d{2}-\d{4}$/;
export const SSN_RE = /^\d{3}-\d{2}-\d{4}$/;
export const TAX_ID_RE = /^\d{9}$/;
export const ROUTING_RE = /^\d{9}$/;
export const DATE_RE = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;

const VALID_STATES = new Set([
  "AA",
  "AB",
  "AE",
  "AK",
  "AL",
  "AP",
  "AR",
  "AS",
  "AZ",
  "BC",
  "CA",
  "CO",
  "CT",
  "DC",
  "DE",
  "FL",
  "FM",
  "GA",
  "GU",
  "HI",
  "IA",
  "ID",
  "IL",
  "IN",
  "KS",
  "KY",
  "LA",
  "MA",
  "MB",
  "MD",
  "ME",
  "MH",
  "MI",
  "MN",
  "MO",
  "MP",
  "MS",
  "MT",
  "NB",
  "NC",
  "ND",
  "NE",
  "NH",
  "NJ",
  "NL",
  "NM",
  "NS",
  "NT",
  "NU",
  "NV",
  "NY",
  "OH",
  "OK",
  "ON",
  "OR",
  "PA",
  "PE",
  "PR",
  "PW",
  "QC",
  "RI",
  "SC",
  "SD",
  "SK",
  "TN",
  "TX",
  "UT",
  "VA",
  "VI",
  "VT",
  "WA",
  "WI",
  "WV",
  "WY",
  "YT",
]);

export function isValidPhone(phone: string): boolean {
  if (!PHONE_RE.test(phone)) return false;

  const digits = phone.replace(/\D/g, "");

  // Reject 111-111-1111, 222-222-2222, etc.
  if (/^(\d)\1+$/.test(digits)) return false;

  return true;
}

export function isValidDate(date: string): boolean {
  if (!DATE_RE.test(date)) return false;

  const [month, day, year] = date.split("/").map(Number);
  const d = new Date(year, month - 1, day);

  return (
    d.getFullYear() === year &&
    d.getMonth() === month - 1 &&
    d.getDate() === day
  );
}

export function isValidState(state: string): boolean {
  return VALID_STATES.has(state.toUpperCase());
}

// ---- Semantic validators ---------------------------------------------------
//
// CardConnect rejects malformed routing numbers and obvious test patterns at
// boarding time with HTTP 400s. Catching them client-side before submit
// avoids the round-trip + the "what went wrong?" black box that follows.
//
// These are deliberately conservative: false positives here would block real
// merchants. When in doubt the validators return "looks legit" (null) and
// let the server have the final word.

/**
 * Validates a US bank routing number against the ABA check-digit algorithm.
 * The 9th digit is a checksum over the first 8 using the weight pattern
 * 3-7-1-3-7-1-3-7 (see ABA Routing Number §3). Returns true if the input
 * is exactly 9 digits AND the checksum matches.
 *
 * CardConnect rejects routing numbers that fail this with an opaque 400 —
 * catch it here so the operator gets an actionable inline error instead.
 */
export function isValidRoutingNumber(routing: string): boolean {
  const digits = (routing || "").replace(/\D/g, "");
  if (digits.length !== 9) return false;
  const weights = [3, 7, 1, 3, 7, 1, 3, 7, 1];
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(digits[i], 10) * weights[i];
  }
  return sum % 10 === 0;
}

/**
 * Detects obvious test/placeholder SSN patterns that CardConnect rejects:
 *   - all-zero blocks (000-xx-xxxx / xxx-00-xxxx / xxx-xx-0000)
 *   - SSA-reserved 9xx series (assigned to ITINs, not real SSNs)
 *   - well-known test patterns (123-45-6789, 111-11-1111, etc.)
 *   - sequential digits forward or backward
 *   - all the same digit (xxxxxxxxx)
 *
 * Returns a human-readable reason if the SSN looks fake, or null if it
 * looks legitimate. Call sites should surface the reason to the user
 * verbatim — the message is already written in friendly form.
 */
export function detectFakeSSN(ssn: string): string | null {
  const digits = (ssn || "").replace(/\D/g, "");
  if (digits.length !== 9) {
    // Wrong length is caught by the SSN_RE check upstream; treat as
    // "looks fine" here so the upstream error is the one shown.
    return null;
  }
  // All the same digit (000000000, 111111111, ..., 999999999).
  if (/^(\d)\1{8}$/.test(digits))
    return "This looks like test data — enter the actual SSN.";
  // Sequential ascending or descending.
  if (digits === "123456789" || digits === "987654321")
    return "This looks like test data — enter the actual SSN.";
  // SSA-invalid: starts with 9 (ITIN range, not a real SSN for merchants).
  if (digits[0] === "9")
    return "SSNs starting with 9 aren't issued to individuals. Enter the actual SSN.";
  // SSA-invalid: starts with 666.
  if (digits.startsWith("666"))
    return "SSNs starting with 666 aren't issued. Enter the actual SSN.";
  // Any group of zeros — SSA never issues these.
  const area = digits.slice(0, 3);
  const group = digits.slice(3, 5);
  const serial = digits.slice(5);
  if (area === "000" || group === "00" || serial === "0000")
    return "SSNs don't contain a 000 area, 00 group, or 0000 serial. Enter the actual SSN.";
  return null;
}

/**
 * Detects obvious test/placeholder EIN patterns. EINs are 9 digits formatted
 * as XX-XXXXXXX (the prefix identifies the IRS campus that issued it).
 * Returns a human-readable reason if fake, null if legitimate-looking.
 *
 * Test patterns flagged:
 *   - all zeros / all same digit
 *   - sequential 123456789 / 987654321
 *   - 00 prefix (IRS doesn't issue these)
 */
export function detectFakeEIN(ein: string): string | null {
  const digits = (ein || "").replace(/\D/g, "");
  if (digits.length !== 9) return null;
  if (/^(\d)\1{8}$/.test(digits))
    return "This looks like test data — enter the actual EIN.";
  if (digits === "123456789" || digits === "987654321")
    return "This looks like test data — enter the actual EIN.";
  // IRS-invalid prefixes: 00 is never issued. The IRS publishes the
  // valid-prefix list (Pub 1635 Appendix); 00 is the easy catch-all
  // that almost always signals a placeholder.
  if (digits.startsWith("00"))
    return "EINs don't start with 00 — enter the actual EIN.";
  return null;
}

/**
 * Returns age in whole years from an MM/DD/YYYY string, or null if the
 * string isn't parseable. Anniversary-aware: someone born 06/15/2007 is
 * 18 on 2025-06-15, not 2025-06-14.
 */
export function ageFromDob(dobMmDdYyyy: string): number | null {
  if (!isValidDate(dobMmDdYyyy)) return null;
  const [m, d, y] = dobMmDdYyyy.split("/").map(Number);
  const today = new Date();
  let age = today.getFullYear() - y;
  const monthDiff = today.getMonth() - (m - 1);
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < d)) {
    age--;
  }
  return age;
}

/**
 * Returns true if the MM/DD/YYYY string is strictly after today.
 * Unparseable strings return false (the date-format check happens
 * separately via isValidDate; this validator is just for the future-check).
 */
export function isFutureDate(mmDdYyyy: string): boolean {
  if (!isValidDate(mmDdYyyy)) return false;
  const [m, d, y] = mmDdYyyy.split("/").map(Number);
  const input = new Date(y, m - 1, d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return input.getTime() > today.getTime();
}

/**
 * Returns true if the MM/DD/YYYY string is before the given year.
 * Useful for business-start-date sanity (no businesses founded pre-1850).
 */
export function isBeforeYear(mmDdYyyy: string, year: number): boolean {
  if (!isValidDate(mmDdYyyy)) return false;
  const [, , y] = mmDdYyyy.split("/").map(Number);
  return y < year;
}

/** MM/DD/YYYY -> YYYY-MM-DD. Empty string when unparseable. */
export function mdyToIso(mmDdYyyy: string): string {
  if (!isValidDate(mmDdYyyy)) return "";
  const [m, d, y] = mmDdYyyy.split("/");
  return `${y}-${m}-${d}`;
}

/** YYYY-MM-DD -> MM/DD/YYYY. Empty string when unparseable. */
export function isoToMdy(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return match ? `${match[2]}/${match[3]}/${match[1]}` : "";
}

/** Today as YYYY-MM-DD in the operator's local timezone. */
export function todayIso(): string {
  const now = new Date();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${mm}-${dd}`;
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

export interface DateFieldRules {
  /** Inclusive lower bound, YYYY-MM-DD. */
  min?: string;
  /** Inclusive upper bound, YYYY-MM-DD. */
  max?: string;
  /** Minimum whole years between the date and today (owner DOB = 18). */
  minAge?: number;
  /** Field name used to open the range messages. */
  label?: string;
  /** Overrides the generic minAge wording. */
  minAgeMessage?: string;
}

/**
 * The one date rule set — shared by the field's own blur check and by the
 * page validators, so a date can't pass on blur and then fail on submit
 * with different wording.
 *
 * Returns null for an empty value; "this field is required" stays with the
 * caller, which knows whether the field is required in its context.
 */
export function dateFieldError(
  value: string,
  rules: DateFieldRules = {},
): string | null {
  const v = value.trim();
  if (!v) return null;

  const { min, max, minAge, label = "Date", minAgeMessage } = rules;

  if (!DATE_RE.test(v)) return "Use a valid date (MM/DD/YYYY).";
  if (!isValidDate(v)) {
    // DATE_RE already bounded the month to 12 and the day to 31, so the only
    // way to land here is a day that month doesn't have (including Feb 29 in
    // a non-leap year). Say which, rather than blaming the format.
    const [month, day, year] = v.split("/").map(Number);
    return `${MONTH_NAMES[month - 1]} ${year} doesn't have ${day} days.`;
  }

  // ISO strings compare correctly as plain strings.
  const iso = mdyToIso(v);

  if (max && iso > max) {
    return max === todayIso()
      ? `${label} can't be in the future.`
      : `${label} can't be after ${isoToMdy(max)}.`;
  }

  if (min && iso < min) {
    return `${label} can't be before ${isoToMdy(min)}.`;
  }

  if (minAge != null) {
    const age = ageFromDob(v);
    if (age !== null && age < minAge) {
      return minAgeMessage ?? `Must be at least ${minAge} years ago.`;
    }
  }

  return null;
}

/**
 * Rules for the two date fields on an application. Called rather than
 * declared as constants because `max` is "today" — a portal left open
 * overnight would otherwise keep yesterday's bound.
 */
export function businessStartDateRules(): DateFieldRules {
  return {
    // CardConnect accepts any past date; 1850 just catches typo'd years.
    min: "1850-01-01",
    max: todayIso(),
    label: "Business start date",
  };
}

export function ownerDobRules(): DateFieldRules {
  return {
    min: "1900-01-01",
    max: todayIso(),
    minAge: 18,
    label: "Date of birth",
    minAgeMessage: "Owner must be at least 18 years old.",
  };
}
